<script>
    import {csAvailableSupported, eventBridge, loginLastToken, loginLastTokens, loginTokens, resourcesPath} from "../../stores.js";
    import {onMount} from "svelte";
    import {DropdownToggle, Dropdown, DropdownMenu, DropdownItem} from "sveltestrap";
    import TokenLabel from "../snippets/TokenLabel.svelte";

    export let selectedToken;

    let isOpen = false;
    let tokenOptions;
    let tokenOptionIndex;
    let invalidTokenFeedback = '';
    let emptyLabel;

    onMount(() => {
        addTokenListListeners();
        document.getElementById("token-selection").focus();
        document.addEventListener("keydown", handleDropdownKeyboardOperations);
        tokenOptions = document.getElementsByClassName("token-option");

        return () => {
            document.removeEventListener("keydown", handleDropdownKeyboardOperations);
            removeTokenListListeners();
        };
    });

    function addTokenListListeners() {
        $eventBridge.addEventListener("cs:AvailableSecurityTokenList", handleAvailableSecurityTokenList);
        $eventBridge.dispatchEvent("cs:getAvailableSecurityTokenList");
        $eventBridge.dispatchEvent("cs:subscribeCardEvent");
    }

    function removeTokenListListeners() {
        $eventBridge.removeEventListener("cs:AvailableSecurityTokenList", handleAvailableSecurityTokenList);
    }

    function handleAvailableSecurityTokenList(event) {
        const payload = JSON.parse(event.payload);
        const tokensFromPayload = payload.securityTokenList;
        $loginLastTokens = $loginTokens;
        $loginTokens = tokensFromPayload;
        selectedToken = calcSelectedToken();
        updateInvalidTokenFeedback();
    }


    function calcSelectedToken() {
        const currentToken = selectedToken;
        const availableTokens = $loginTokens.filter(isAvailable);

        if (availableTokens.length === 1) { // auto-select only element of singleton list
            return availableTokens[0];
        }

        const newHwToken = findNewHwToken();

        if (newHwToken) { // auto-select new hw token
            return newHwToken;
        }

        const token = $loginTokens.find(e => e.fingerprint === currentToken?.fingerprint);

        if (!token) { // current token is not in the list anymore
            return currentToken?.type === 'HW' ? findElement($loginTokens, $loginLastToken) : null;
        }

        return currentToken;
    }

    function findNewHwToken() {
        const isAvailableHwToken = (t) => t.type === "HW" && t.state === "AVAILABLE";
        const isEqual = (t1, t2) => t1.fingerprint === t2.fingerprint;
        const hwTokens = $loginTokens.filter(t => isAvailableHwToken(t) && !$loginLastTokens.some(l => isEqual(l, t)));
        return hwTokens.length > 0 ? hwTokens[0] : null;
    }

    function findElement(tokenList, token) {
        const elems = $loginTokens.filter(t => t.fingerprint === token?.fingerprint);
        return elems.length > 0 ? elems[0] : null;
    }

    function isAvailable(token) {
        return token?.state === 'AVAILABLE';
    }

    function handleDropdownKeyboardOperations(event) {
        if (event.key === "ArrowUp") {
            tokenOptionIndex = (--tokenOptionIndex + tokenOptions.length) % tokenOptions.length;
            tokenOptions[tokenOptionIndex].focus();
        }

        if (event.key === "ArrowDown") {
            tokenOptionIndex = ++tokenOptionIndex % tokenOptions.length;
            tokenOptions[tokenOptionIndex].focus();
        }

        if (event.key === "Escape") {
            isOpen = false;
            document.getElementById("token-selection").focus();
        }
    }

    function toggleDropdown() {
        if (tokenOptions.length === 0) {
            return;
        }

        isOpen = !isOpen;
    }

    function focusFirstTokenOption() {
        if (tokenOptions.length === 0) {
            return;
        }

        setTimeout(() => {
            tokenOptionIndex = 0;
            tokenOptions[tokenOptionIndex].focus();
        }, 50);
    }

    function selectToken(token) {
        if (token.type === "SW") {
            $loginLastToken = token;
        }

        selectedToken = token;
        updateInvalidTokenFeedback();

        document.getElementById("token-selection").focus();
    }

    function updateInvalidTokenFeedback() {
        if (!selectedToken || selectedToken?.state === 'AVAILABLE') {
            invalidTokenFeedback = '';
        } else if (selectedToken?.state === 'ERROR_EXPIRED') {
            invalidTokenFeedback = "Das Zertifikat ist abgelaufen. Bitte nutzen Sie ein anderes Zertifikat für die Anmeldung."
        } else {
            invalidTokenFeedback = "Die Anmeldung mit diesem Zertifikat ist nicht möglich. Bitte führen Sie ggf. zuerst die Erstregistrierung durch oder nutzen Sie ein anderes Zertifikat für die Anmeldung.";
        }
    }

    $: validSelectedToken = selectedToken?.state === "AVAILABLE";

    $: if ($csAvailableSupported) {
        emptyLabel = $loginTokens.length ? "(beA)-Karte oder Software-Zertifikat auswählen" : "kein geeignetes Hardware- oder Software-Zertifikat gefunden";
    } else {
        emptyLabel = 'Warten auf Verbindung zur Client Security';
    }
</script>

<div id="token-selection-dropdown" {...$$restProps}>
    <Dropdown
        {isOpen}
        toggle={toggleDropdown}
    >
        <DropdownToggle
            id="token-selection"
            data-test-name="selectToken-select-token"
            class={selectedToken && !validSelectedToken ? "invalid" : ""}
            on:click={focusFirstTokenOption}
        >
            <div
                id="token-description"
                data-test-name="selectToken-select-token-selected"
                class:invalid={!validSelectedToken}
            >
                <TokenLabel token={selectedToken} {emptyLabel} />
            </div>
            <div id="dropdown-toggle-icon">
                <img
                    id="dropdrown-toggle-icon"
                    src={`${$resourcesPath}/img/drop-down.png`}
                    alt="dropdown toggle icon"
                />
            </div>
        </DropdownToggle>
        <DropdownMenu
            id="token-options"
            data-test-name="selectToken-select-token-options"
        >
            {#each $loginTokens as token (token.fingerprint)}
                <DropdownItem
                    class="token-option {token.state !== 'AVAILABLE' ? 'invalid' : ''}"
                    data-test-name="selectToken-select-token-option-{token.fingerprint}"
                    tabindex="-1"
                    on:click={selectToken(token)}
                >
                    <TokenLabel {token} />
                </DropdownItem>
            {/each}
        </DropdownMenu>
    </Dropdown>

    <div
        class:feedback={selectedToken && !validSelectedToken}
        class="invalid-feedback"
        data-test-name="selectToken-select-token-feedback"
    >
        {invalidTokenFeedback}
    </div>
</div>

<style>
    #token-selection-dropdown {
        text-align: left;
    }

    #token-selection-dropdown :global(#token-selection) {
        width: 100%;
        text-align: left;
        background-color: var(--brak-greyed-white);
        padding: 0.375rem 0.75rem;
        border: var(--bs-border-width) solid var(--brak-greyed-white);
        border-radius: 0;
    }

    #token-selection-dropdown :global(#token-selection:focus-visible) {
        box-shadow: 0 0 0 0.25rem var(--brak-medium-blue);
    }

    #token-selection-dropdown :global(#token-selection.invalid) {
        border: var(--bs-border-width) solid var(--brak-red);
    }

    #token-selection-dropdown :global(#token-description) {
        color: var(--brak-black);
        width: 97%;
        display: inline-block;
    }

    #token-selection-dropdown :global(#token-description.invalid) {
        color: var(--brak-dark-grey);
    }

    #token-selection-dropdown :global(#dropdown-toggle-icon) {
        width: 2%;
        display: inline-block;
    }

    #token-selection-dropdown :global(#token-options) {
        width: 100%;
        padding: 0;
        border: 1px solid gray;
        border-radius: 0;
    }

    #token-selection-dropdown :global(.token-option) {
        color: var(--brak-black);
        background-color: var(--brak-white);
        padding: 0 0.75rem;
        margin: 0;
        border: 1px solid var(--brak-white);
        border-radius: 0;
    }

    #token-selection-dropdown :global(.token-option:hover) {
        background-color: var(--brak-light-blue);
        border: 1px solid var(--brak-dark-blue);
    }

    #token-selection-dropdown :global(.token-option:focus-visible) {
        background-color: var(--brak-light-blue);
        outline: none;
        border: 1px solid var(--brak-dark-blue);
    }

    #token-selection-dropdown :global(.token-option.invalid) {
        background-color: var(--brak-light-grey);
    }

    #token-selection-dropdown :global(.token-option.invalid:hover) {
        background-color: var(--brak-medium-grey);
    }

    #token-selection-dropdown :global(.token-option.invalid:focus-visible) {
        background-color: var(--brak-medium-grey);
    }

    #token-selection-dropdown :global(.token-icon) {
        display: inline-block;
        color: var(--brak-dark-blue);
    }

    #token-selection-dropdown :global(.token-label) {
        font-weight: normal;
    }

    .invalid-feedback {
        display: block;
        visibility: hidden;
        line-height: var(--bs-body-line-height);
    }

    .feedback.invalid-feedback {
        visibility: initial;
    }
</style>